import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAction } from '@limuzin/kit';
import { removeAccount } from '../api';
import { useSession } from '../session';
import { Button, ErrorNote, EyeButton, LabelledField, Sheet } from '../widgets';

/** Where «Удалить аккаунт» opens: what Google Play and RuStore are given (ADR 0048). */
export const REMOVE_ACCOUNT_PATH = '/profile/remove';

/**
 * Closes the sheet the way it was opened: back, when a tap in the app pushed it, so
 * the history holds no second [under]; or onto [under], when the link opened the tab
 * on it and there is nothing to go back to.
 */
export function useCloseRemoval(under: string): () => void {
  const navigate = useNavigate();
  const { key } = useLocation();
  return () => (key === 'default' ? navigate(under, { replace: true }) : navigate(-1));
}

/**
 * «Удалить аккаунт» (ADR 0048), from «Профиль» and from «Согласие»: what goes and what
 * stays, the password, and one red button. A port of `remove_account_sheet.dart`.
 *
 * Only the server can erase, so it needs a connection and says so when there is none
 * (ADR 0030). Done, the session is gone on the server already, and signing out here is
 * what lands the tab back on «Вход».
 */
export function RemoveAccountSheet({ onClose }: { onClose: () => void }) {
  const { api, signOut } = useSession();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);

  const remove = useAction(async () => {
    if (!password) return;
    await removeAccount(api, password);
    // Off this address first: left on it, whoever signs in next lands on the sheet.
    navigate('/', { replace: true });
    signOut();
  });

  return (
    <Sheet
      title="Удалить аккаунт"
      hint="Имя, номер, фото, сохранённые адреса и переписка удалятся навсегда. Поездки останутся в истории сервиса, но без вашего имени и номера. Восстановить аккаунт будет нельзя — только зарегистрироваться заново."
      onClose={onClose}
    >
      <LabelledField
        label="Пароль"
        type={shown ? 'text' : 'password'}
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        trailing={<EyeButton shown={shown} onClick={() => setShown(!shown)} />}
      />
      <ErrorNote error={remove.error} />
      <div style={{ marginTop: 14 }}>
        <Button
          tone="stop"
          size="card"
          icon="delete_outline"
          busy={remove.pending}
          disabled={!password}
          onClick={() => void remove.run()}
        >
          Удалить навсегда
        </Button>
      </div>
    </Sheet>
  );
}
